import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Leaf, ArrowLeft, Copy } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      setResult(r.data);
      if (r.data.email_sent) {
        toast.success("Check your inbox for the reset link.");
      } else if (r.data.reset_url) {
        toast.message("Email service not configured — link shown below.");
      } else {
        toast.success("If that email exists, a reset link was created.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.reset_url);
      toast.success("Link copied");
    } catch {
      window.prompt("Copy this link:", result.reset_url);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link
          to="/admin/login"
          className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#1a2947] mb-8"
          data-testid="back-login-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
        <div className="bg-white border border-[#e6e2d6] rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <img src="/bailey-logo.png" alt="Bailey Family Reunion" className="h-10 w-10 rounded-full object-cover ring-2 ring-[#1a2947]/10" />
            <span className="font-serif text-2xl text-stone-900">Bailey Family Reunion</span>
          </div>
          <div className="label-eyebrow mb-2">Forgot password</div>
          <h1 className="font-serif text-3xl text-stone-900 mb-6">
            Reset it in a jiffy.
          </h1>

          {!result?.reset_url ? (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="fp-email" className="label-eyebrow">Email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 bg-stone-50 border-stone-200"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill h-11"
                data-testid="forgot-submit-btn"
              >
                {loading ? "Working..." : "Send reset link"}
              </Button>
            </form>
          ) : (
            <div>
              <p className="text-stone-600 text-sm mb-3">
                Email delivery isn't configured yet. Copy this reset link and
                open it in your browser (valid 30 minutes):
              </p>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs break-all text-stone-800">
                {result.reset_url}
              </div>
              <div className="mt-4 flex gap-2">
                <Button onClick={copyLink} className="rounded-full btn-pill" data-testid="copy-reset-link">
                  <Copy className="h-4 w-4 mr-1" /> Copy link
                </Button>
                <a href={result.reset_url}>
                  <Button variant="outline" className="rounded-full">
                    Open now
                  </Button>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
